import React, { useEffect, useRef, useState } from 'react';
import { getBannerTranscodingStatus } from '../../Services/services';

export default function BannerTranscodingProgress({ bannerId, jobId, onFinished }) {
  const [result, setResult] = useState({ status: 'processing', progress_percent: null });
  const [retry, setRetry] = useState(0);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  useEffect(() => {
    let cancelled = false;
    let timer;
    let errors = 0;
    setResult({ status: 'processing', progress_percent: null });
    const poll = async () => {
      try {
        const response = await getBannerTranscodingStatus(bannerId);
        if (cancelled) return;
        const data = response.data.data || response.data.responseDetails;
        if (!data?.status) throw new Error('Missing transcoding status');
        errors = 0;
        setResult(data);
        if (['complete', 'failed', 'not_started'].includes(data.status)) {
          finishedRef.current(data);
          return;
        }
      } catch (error) {
        if (cancelled) return;
        errors += 1;
        if ([403, 404].includes(error?.response?.status) || errors >= 5) {
          setResult({ status: 'poll_error', progress_percent: null });
          return;
        }
      }
      // Schedule after the request finishes so slow AWS requests never overlap.
      timer = setTimeout(poll, 5000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bannerId, jobId, retry]);

  const running = ['processing', 'finalizing'].includes(result.status);
  const progress = Number.isFinite(result.progress_percent) ? result.progress_percent : null;
  return (
    <div className="banner-media-box mb-3" aria-live="polite">
      {running && <>
        <p className="mb-2">{result.status === 'finalizing' ? 'Finalizing video preview…'
          : `Transcoding banner video…${progress !== null ? ` ${progress}%` : ''}`}</p>
        <div className="banner-progress-track" role="progressbar" aria-label="Banner transcoding progress"
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress !== null ? progress : undefined}>
          <div className={`banner-progress-fill${progress === null ? ' banner-progress-indeterminate' : ''}`}
            style={{ width: progress !== null ? `${progress}%` : '35%' }} />
        </div>
        <small className="text-muted">Banner stays inactive until approved.</small>
      </>}
      {result.status === 'complete' && <p className="text-success mb-0">Transcoding complete. Preview the video, then activate the banner.</p>}
      {result.status === 'failed' && <p className="text-danger mb-0" role="alert">Transcoding failed. The banner remains inactive. Upload a new video or regenerate the trailer.</p>}
      {result.status === 'not_started' && <p className="text-danger mb-0" role="alert">Transcoding has not started. The banner remains inactive.</p>}
      {result.status === 'poll_error' && <>
        <p role="alert">Unable to check progress. Transcoding may still be running.</p>
        <button type="button" className="btn btn-secondary" onClick={() => setRetry(value => value + 1)}>Retry status check</button>
      </>}
    </div>
  );
}
